'use client';

import { ProcessedVesselData } from '@/types/api';
import { useState, useEffect } from 'react';

interface VesselTableRowProps {
  vessel: ProcessedVesselData;
  index: number;
}

export default function VesselTableRow({ vessel, index }: VesselTableRowProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), index * 40);
    return () => clearTimeout(timer);
  }, [index]);

  const ShipIcon = () => (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
    </svg>
  );

  const statusLower = (vessel.status || '').toLowerCase();
  const isOngoing = statusLower === 'ongoing' || statusLower === 'sailing';
  const isManeuver = statusLower === 'maneuver';

  return (
    <li
      className={`py-3 flex items-center justify-between gap-3 transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div
          className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
            isOngoing
              ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400'
              : isManeuver
              ? 'bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
          }`}
        >
          <ShipIcon />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {vessel.vessel}
            </span>

            {/* Movement Status Badge */}
            {isOngoing && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Ongoing {vessel.speed !== undefined && vessel.speed > 0 ? `• ${vessel.speed} kn` : ''}
              </span>
            )}
            {isManeuver && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                Maneuver {vessel.speed !== undefined && vessel.speed > 0 ? `• ${vessel.speed} kn` : ''}
              </span>
            )}
            {!isOngoing && !isManeuver && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                Stopped
              </span>
            )}

            {/* Hidden / Shown Badge */}
            {vessel.is_hidden ? (
              <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200/50">
                Hidden
              </span>
            ) : (
              <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200/50">
                Shown
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
            ID: {vessel.vessel_id}
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap text-right shrink-0">
        {vessel.timeAgo}
      </div>
    </li>
  );
}
